/**
 * utils/contactImportSalesMap.js
 * Contacts xlsx import, batch 4 of 5 (the SALESPERSON MAPPING LAYER): sits between the resolution
 * layer (`contactImportResolve.js`, batch 3a) and the commit layer (`contactImportCommit.js`,
 * batch 2). Turns the raw Sales names batch 3a left on each plan row (`salesNames: string[]`)
 * into real `data.persons` ids, producing the plan `commitContactImport` actually expects
 * (`personIds`, not `salesNames`). No React, no UI, no writes to storage — batch 5 builds the
 * screen that asks a human these questions and feeds the answers back in as `decisions`.
 *
 * ── PERSONS ONLY — NEVER A ROLE, NEVER A BRANCH QUESTION ───────────────────────────────────────
 * A Contact links to a PERSON, never a role (`Contact.personIds` names people — see the root
 * doc's Contact entry and `utils/contacts.js`). This layer creates `data.persons` records ONLY.
 * It never touches `data.salespeople`, never asks which branch, and never creates a role — a role
 * is created later, at transaction time, through the existing add-role path
 * (`SalespersonFormModal`/`createSalespersonRole`). A person with zero roles is a fully valid,
 * already-supported state (`KelolaSalesModal`'s "Orang Tanpa Peran" section exists for exactly
 * this). "UMKM Amin"/"UMKM Te Tjun" are ordinary people here, same as any other name — the client
 * uses them as a routing label, the app gives them no special behavior, and this layer builds
 * none.
 *
 * ── THE REAL FILE'S NINE DECISIONS ──────────────────────────────────────────────────────────────
 * CABANG (uppercase): SASONGKO, BUDI, AKWET, AMIN, ABIE, TE TJUN. PUSAT (mixed case): Sasongko,
 * Akwet, Amin, Te Tjun, Abie, Budi, plus GAS, UMKM Amin, UMKM Te Tjun. **Case never makes two
 * names distinct** — "BUDI" and "Budi" are the SAME decision, merged case/whitespace-insensitively
 * across BOTH sheets (`salesNameKey`) — the parser's own `stats.salesUsage` only merges WITHIN one
 * sheet (see that file's own header comment); this layer is what merges across sheets too. The six
 * standard names collapse into six decisions; GAS/UMKM Amin/UMKM Te Tjun are three more genuinely
 * distinct names — nine decisions in total against the real file.
 *
 * ── THE DECISION, PER NAME ───────────────────────────────────────────────────────────────────────
 * The app NEVER matches a person by name on its own — every name is presented to a human, who
 * picks exactly one of:
 *   - `{action:"map", personId}` — this Sales name IS an existing person, by id.
 *   - `{action:"create", name, code}` — a brand-new person. `name`/`code` are BOTH supplied by the
 *     human (never guessed here) — `code` must satisfy `persons.js`'s own `addPerson` contract
 *     (1-4 chars, [A-Z0-9] after uppercasing, globally unique across every person) — this layer
 *     never invents one, the same way `contactImportResolve.js` never invents an address or a
 *     city. The code prints on invoices; it belongs to the user, not a heuristic.
 *   - `{action:"skip"}` — no person for this name. Every shop relying ONLY on this name ends up
 *     with zero personIds and is reported EXCLUDED (see below) — never silently dropped, and never
 *     conflated with a shop that has at least one OTHER, still-usable name.
 *
 * **Presence, not truthiness, is what distinguishes "unanswered" from "answered but blank"** — the
 * same discipline `contactImportResolve.js` established. Every read goes through `readDecision`
 * (`key in map`, never `map[key] || fallback`); `normalizeSalesMapDecision` destructures a raw
 * decision value the same defensive way that file's own `normalize*Decision` exports do (a bare
 * string for `action` alone, or a full object). `salesNameKey(name)` is the ONE stable, exported
 * key builder — batch 5 must build/read decision keys through it, never by hand, exactly the
 * discipline `duplicateGroupKey`/`shopKey` already established in the resolution layer.
 *
 * **`decisions` here is a single FLAT map, `{[salesNameKey(name)]: rawDecision}`** — deliberately
 * simpler than `contactImportResolve.js`'s five nested sub-maps, since this batch resolves exactly
 * one kind of ambiguity (which real person a Sales name refers to), not five.
 *
 * ── COLLISION SAFETY NET — PLAIN NAME MATCH, NOT `detectPersonCollision` ───────────────────────
 * `persons.js`'s `detectPersonCollision` is built for `SalespersonFormModal`'s own flow, which
 * always creates a person AND a role (at a known branch) together — its `"add-person"` change
 * kind takes a `branchId` for exactly that reason, and its core rule requires BOTH the new and the
 * existing person's ACTIVE role-sets to be non-empty before it will ever flag anything. This batch
 * creates persons with NO role at all, so there is no real `branchId` to supply and a brand-new,
 * roleless person's active role-set is always empty — reusing that function here literally would
 * never be able to fire, regardless of what placeholder `branchId` was passed in, which would make
 * the "safety net" silently inert.
 *
 * So this layer runs its OWN, narrower, PLAIN NAME check instead — `detectSalesPersonNameCollision`
 * — same detection-only contract (never merges, never blocks, never concludes two records are the
 * same person; two people are legally allowed to share a name everywhere else in this app, and
 * that stays true here). It fires on every `"create"` decision, comparing the new person's name
 * against every EXISTING person's current name (case/whitespace-insensitive), and reports every
 * match it finds — `{ambiguous:true, name, matches:[{id,name,code}]}` — so batch 5 can say "a
 * person named Budi already exists — did you mean to use them instead?" and offer switching that
 * one decision to `{action:"map", personId: matches[0].id}`. This is the actual mistake the check
 * exists to catch, so `matches` always carries the real ids/codes needed to build that offer, not
 * just a name string to look at.
 *
 * ── COMPOSITION — how this chains with batch 3a and batch 2 ────────────────────────────────────
 * `resolveSalesPersonMapping(existingData, plan, decisions)` takes batch 3a's own `plan` output
 * (`resolveContactImport`'s `plan`, each row still carrying `salesNames` and no `personIds`) plus
 * this batch's own `decisions`, and returns:
 *   - `data` — `existingData` with every newly-created person ADDED, via the pure `addPerson`
 *     (`persons.js`) — never `App.js`'s handler, never a second implementation of person-creation.
 *   - `plan` — the SAME rows, `salesNames` replaced by a real `personIds: string[]`, ready to hand
 *     straight to `commitContactImport` exactly as its own header comment expects.
 *   - `unresolved` — every Sales name still needing a decision (one entry per NAME, not per shop —
 *     see below for why that's the right unit), each naming which shops/sheets it blocks.
 *   - `excluded` — every shop that reached a fully-decided state but ended up with zero personIds
 *     (every one of its names was skipped) — same shape/spirit as batch 3a's own `excluded`.
 *   - `collisions` — every `detectSalesPersonNameCollision` hit found while creating a person.
 *   - `readyToCommit` — `unresolved.length === 0` (mirrors batch 3a; `excluded`/`collisions` never
 *     block it — both are fully-resolved, deliberate outcomes, not pending questions).
 *
 * Batch 5's own chain, in order, is exactly:
 *   const { plan: plan3a } = resolveContactImport(parsed, decisions3a);           // batch 3a
 *   const { data: dataWithPersons, plan: plan4, excluded, collisions } =
 *     resolveSalesPersonMapping(data, plan3a, decisions4);                        // batch 4 (this file)
 *   const { data: dataWithContacts, report } = commitContactImport(dataWithPersons, plan4); // batch 2
 *   update(() => dataWithContacts);                                              // ONE update()
 * Nothing is written to the app's real `data` until that single final `update()` call — this
 * layer's own returned `data` is a plain, uncommitted object like every other pure function in
 * this codebase; the caller decides whether/when to actually commit it.
 *
 * **A newly-created person's id does not exist until `addPerson` actually runs — this layer
 * resolves that id BEFORE it is needed anywhere else, by processing one decision at a time against
 * an ACCUMULATING `data`.** Each `"create"` decision calls `addPerson(data, {name, code})`.
 * `addPerson` returns the SAME reference on every refusal (blank name, bad code format, a code
 * collision) and only ever a genuinely NEW object on success, so that reference check
 * (`nd === data`) is what this layer gates the whole branch on FIRST — never a step that could be
 * skipped. Only past that gate does it need to know WHICH person was just created, and even then
 * it never reads list position (`nd.persons[nd.persons.length - 1]`) to find out — `findPersonByCode`
 * looks the new person up by the very code just supplied, since a code is globally unique across
 * every person (`persons.js`). This is deliberate, not incidental: trusting array position would
 * be exactly the class of bug this app already fixed once in `ContactPicker`'s own create row (ask
 * to create, silently get attached to someone else) — reading back by the one globally-unique
 * fact about the record just created cannot resolve to the wrong person, regardless of where
 * `addPerson` happens to insert. A lookup miss here (should not be reachable through
 * `addPerson`'s own current contract) is treated as a failed creation, never a silent fallback to
 * whichever person happens to be nearby in the array. This same threading is what makes two
 * decisions minting the same `code` in one run behave correctly with zero extra logic: the second
 * `addPerson` call already sees the first decision's newly-added person in its own
 * `data.persons`, so its own code-uniqueness check refuses it exactly the way it would refuse any
 * other real collision.
 *
 * ── WHAT THIS DOES NOT DO ───────────────────────────────────────────────────────────────────────
 * - Never creates a role, never asks for a branch, never calls anything in `salespeople.js`.
 * - Never invents a code — a missing/invalid/colliding code on a `"create"` decision is reported,
 *   never silently substituted or auto-suffixed (unlike `suggestSalesCode`, which is a UI
 *   convenience for a DIFFERENT flow, not something this pure layer reaches for on its own).
 * - Never re-derives which raw Sales names belong to which shop — that grouping is entirely
 *   `plan[i].salesNames`, as batch 3a already built it; this layer only ever reads that array.
 * - Never touches a `possible-second-sales` problem directly — batch 3a's own plan rows already
 *   folded a confirmed second salesperson into a second entry in `salesNames`, so by the time a row
 *   reaches this layer there is nothing special left to notice about that case; it's just a plan
 *   row with two names, resolved like any other row with two names.
 */
import { addPerson, personExists } from "./persons";

function normSalesName(s) {
  return String(s == null ? "" : s).trim().replace(/\s+/g, " ").toUpperCase();
}

/** Stable, cross-sheet, case/whitespace-insensitive key for one Sales name — export so batch 5
 *  never re-derives it (same discipline `contactImportResolve.js`'s `duplicateGroupKey`/`shopKey`
 *  already established). */
export function salesNameKey(name) {
  return normSalesName(name);
}

/** `key in map`, never `map[key] || fallback` — see the file header's own warning; identical in
 *  spirit to `contactImportResolve.js`'s own private helper of the same name. */
function readDecision(map, key) {
  if (!map || !Object.prototype.hasOwnProperty.call(map, key)) {
    return { answered: false, value: undefined };
  }
  return { answered: true, value: map[key] };
}

/** Destructures a raw decision value defensively — a bare string (`action` alone, e.g. `"skip"`)
 *  or a full object. Never assumes a shape without checking `typeof` first. */
export function normalizeSalesMapDecision(raw) {
  const isObj = raw != null && typeof raw === "object";
  const action = isObj ? raw.action : raw;
  const personId = isObj && typeof raw.personId === "string" ? raw.personId : null;
  const name = isObj && typeof raw.name === "string" ? raw.name : "";
  const code = isObj && typeof raw.code === "string" ? raw.code : "";
  return { action, personId, name, code };
}

function normNameKey(s) {
  return String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

/** The one place this module trusts WHICH person a successful `addPerson` call just created.
 *  Deliberately never reads list position (`persons[persons.length-1]`) — a code is globally
 *  unique across every person (`persons.js`), so looking it up by that same code is the one
 *  lookup that can never resolve to the wrong record, regardless of where `addPerson` happens to
 *  insert. Returns `null` (never guesses/falls back to a position) when no such code is found —
 *  the caller must treat that as a failed creation, not silently accept some other person. */
function findPersonByCode(persons, code) {
  const key = String(code == null ? "" : code).trim().toUpperCase();
  if (!key) return null;
  return (persons || []).find((p) => p.code === key) || null;
}

/**
 * DETECTION ONLY — never merges, never concludes two records ARE the same person, and never
 * blocks anything by itself (see the file header's own comment for why this exists instead of
 * reusing `persons.js`'s `detectPersonCollision`). Every existing person (excluding
 * `excludePersonId`, if given) whose CURRENT name matches `name` case/whitespace-insensitively is
 * reported — could be more than one. Returns `{ambiguous:false}` when nothing matches, else
 * `{ambiguous:true, name, matches:[{id,name,code}]}` so a caller (batch 5) has everything it needs
 * to offer "use this existing person instead" without a second lookup.
 */
export function detectSalesPersonNameCollision(persons, name, excludePersonId = null) {
  const key = normNameKey(name);
  if (!key) return { ambiguous: false };
  const matches = (persons || [])
    .filter((p) => p.id !== excludePersonId && normNameKey(p.name) === key)
    .map((p) => ({ id: p.id, name: p.name, code: p.code }));
  if (matches.length === 0) return { ambiguous: false };
  return { ambiguous: true, name, matches };
}

/**
 * Every distinct Sales name still needing a decision, collapsed cross-sheet and case/whitespace-
 * insensitively (`salesNameKey`), from batch 3a's own `plan` (each row's `salesNames: string[]`).
 * Read-only — never mutates `plan`, never decides anything. This is what batch 5's own review
 * screen renders one row per NAME from: for each, which sheet(s) it appears in and how many shops
 * use it, so a human can judge each of the real file's nine decisions on real information rather
 * than a bare string.
 * @returns {{key:string, name:string, variants:string[], sheets:string[], shopCount:number,
 *   shops:{list:string,name:string,anchorRow:number|null}[]}[]} sorted by `shopCount` descending,
 *   then `name` — the most-used names surface first.
 */
export function collectSalesNamesNeeded(plan) {
  const rows = Array.isArray(plan) ? plan : [];
  const byKey = new Map();

  for (const row of rows) {
    const names = Array.isArray(row.salesNames) ? row.salesNames : [];
    const seenKeysOnThisRow = new Set();
    for (const raw of names) {
      const key = salesNameKey(raw);
      if (!key) continue;
      if (!byKey.has(key)) {
        byKey.set(key, { key, variants: [], sheets: new Set(), shops: [] });
      }
      const entry = byKey.get(key);
      if (!entry.variants.includes(raw)) entry.variants.push(raw);
      entry.sheets.add(row.list);
      // A shop is counted once per NAME even if (in principle) the same raw name appeared twice
      // on one row — `salesNames` is already deduped by contactImportResolve.js, but this guards
      // the invariant locally too rather than trusting an upstream file to always hold.
      if (!seenKeysOnThisRow.has(key)) {
        seenKeysOnThisRow.add(key);
        entry.shops.push({
          list: row.list,
          name: row.name,
          anchorRow: row._meta ? row._meta.anchorRow : null,
        });
      }
    }
  }

  return [...byKey.values()]
    .map((e) => ({
      key: e.key,
      name: e.variants[0],
      variants: e.variants,
      sheets: [...e.sheets].sort(),
      shopCount: e.shops.length,
      shops: e.shops,
    }))
    .sort((a, b) => b.shopCount - a.shopCount || a.name.localeCompare(b.name));
}

/**
 * Resolves batch 3a's own `plan` (each row's raw `salesNames`) against a human's per-name
 * `decisions` into the plan `commitContactImport` expects (`personIds`), plus every new person the
 * decisions required. Pure, deterministic; never mutates `existingData`/`plan`.
 * @param {object} existingData — the app's current `data` (reads/extends `.persons` only).
 * @param {object[]} plan — batch 3a's `resolveContactImport(...).plan`.
 * @param {object} decisions — flat `{[salesNameKey(name)]: rawDecision}` — see the file header.
 * @returns {{data:object, plan:object[], unresolved:object[], excluded:object[],
 *   collisions:object[], readyToCommit:boolean}}
 */
export function resolveSalesPersonMapping(existingData, plan, decisions) {
  const rows = Array.isArray(plan) ? plan : [];
  const dec = decisions || {};
  const needed = collectSalesNamesNeeded(rows);

  let data = existingData;
  const unresolved = [];
  const collisions = [];
  const unresolvedKeys = new Set();
  const personIdByKey = new Map();

  const pushUnresolved = (entry, type, message) => {
    unresolvedKeys.add(entry.key);
    unresolved.push({
      type,
      key: entry.key,
      name: entry.name,
      variants: entry.variants,
      sheets: entry.sheets,
      shopCount: entry.shopCount,
      message,
    });
  };

  for (const entry of needed) {
    const { answered, value } = readDecision(dec, entry.key);
    if (!answered) {
      pushUnresolved(
        entry,
        "sales-name-unmapped",
        `Nama sales "${entry.name}" (dipakai ${entry.shopCount} toko di ${entry.sheets.join(" & ")}) belum diputuskan — map ke orang yang sudah ada, buat orang baru, atau lewati.`
      );
      continue;
    }

    const { action, personId, name, code } = normalizeSalesMapDecision(value);

    if (action === "map") {
      if (personId && personExists(data.persons, personId)) {
        personIdByKey.set(entry.key, personId);
      } else {
        pushUnresolved(
          entry,
          "sales-name-invalid-decision",
          `Nama sales "${entry.name}" dipetakan ke orang yang tidak ditemukan — pilih orang yang valid.`
        );
      }
      continue;
    }

    if (action === "create") {
      const personName = (name || entry.name || "").trim();
      const collision = detectSalesPersonNameCollision(data.persons, personName);
      if (collision.ambiguous) {
        collisions.push({ key: entry.key, salesName: entry.name, ...collision });
      }

      const nd = addPerson(data, { name: personName, code });
      if (nd === data) {
        pushUnresolved(
          entry,
          "sales-name-create-failed",
          `Tidak bisa membuat orang baru untuk "${entry.name}" — nama kosong, atau kode "${code}" tidak valid/sudah dipakai orang lain.`
        );
        continue;
      }
      // Never trust list position for WHICH person was just created — look it up by its own
      // (globally unique) code instead. A missing result here would mean addPerson reports
      // success (a new `data` reference) without the code actually landing on any person, which
      // should not be reachable through its own contract — but this module must never guess a
      // different person to link the shop to if that ever drifts, so it fails the creation
      // instead of ever falling back to `nd.persons[nd.persons.length - 1]`.
      const created = findPersonByCode(nd.persons, code);
      if (!created) {
        pushUnresolved(
          entry,
          "sales-name-create-failed",
          `Gagal membuat orang baru untuk "${entry.name}" (sebab tidak diketahui).`
        );
        continue;
      }
      personIdByKey.set(entry.key, created.id);
      data = nd;
      continue;
    }

    if (action === "skip") {
      // Deliberately contributes no personId — see excluded handling below.
      continue;
    }

    // Unrecognized/malformed action — treat exactly like unanswered, never guess (mirrors
    // contactImportResolve.js's identical rule for a malformed duplicate-name `kind`).
    pushUnresolved(
      entry,
      "sales-name-unmapped",
      `Nama sales "${entry.name}" (dipakai ${entry.shopCount} toko di ${entry.sheets.join(" & ")}) belum diputuskan — map ke orang yang sudah ada, buat orang baru, atau lewati.`
    );
  }

  const excluded = [];
  const newPlan = [];

  for (const row of rows) {
    const names = Array.isArray(row.salesNames) ? row.salesNames : [];
    const rowKeys = [];
    for (const raw of names) {
      const key = salesNameKey(raw);
      if (key && !rowKeys.includes(key)) rowKeys.push(key);
    }

    let blocked = false;
    const rowPersonIds = [];
    for (const key of rowKeys) {
      if (unresolvedKeys.has(key)) {
        blocked = true;
        continue;
      }
      const pid = personIdByKey.get(key);
      if (pid && !rowPersonIds.includes(pid)) rowPersonIds.push(pid);
      // A key with no entry in personIdByKey and not in unresolvedKeys was a deliberate "skip" —
      // contributes nothing, on purpose.
    }

    if (blocked) continue; // still waiting on at least one of this row's own names

    if (rowPersonIds.length === 0) {
      excluded.push({
        type: "no-person",
        sheet: row.list,
        row: row._meta ? row._meta.anchorRow : null,
        name: row.name,
        sourceRows: row._meta ? row._meta.sourceRows : null,
        message: `Toko "${row.name}" tidak punya sales/person sama sekali (semua nama sales dilewati) — tidak dibuat sebagai kontak.`,
      });
      continue;
    }

    const { salesNames, ...rest } = row;
    newPlan.push({
      ...rest,
      // Restores the trace fields contactImportCommit.js's own report reads at the TOP level
      // (`row`/`sourceRows` — see that file's own plan-row contract) from where batch 3a actually
      // carries them (`_meta`) — this is the last stop before commit, so this is where that gap
      // closes rather than leaving the final report's traceability silently empty.
      row: row._meta ? row._meta.anchorRow : row.row,
      sourceRows: row._meta ? row._meta.sourceRows : row.sourceRows,
      personIds: rowPersonIds,
    });
  }

  return {
    data,
    plan: newPlan,
    unresolved,
    excluded,
    collisions,
    readyToCommit: unresolved.length === 0,
  };
}
